import React, { useState, useEffect } from "react";
import { Typography, Box, Grid, Dialog, DialogContent, useMediaQuery, Button, Pagination, Tab } from "@mui/material";
import { makeStyles } from '@mui/styles';
import RoundCloseIcon from "../../icons/RoundCloseIcon.svg";
import RoundBackIcon from "../../icons/RoundBackIcon.svg";
import CheckBox from "../../icons/CheckBox.svg"
import UnCheckedBox from "../../icons/UnCheckedBox.svg"
import formatNumber from "../util/formatWithCommas";
import { format } from "date-fns";
import { TransactionDTO } from "@inosio/payments-sdk";
import { TabContext, TabList, TabPanel } from '@mui/lab';

const useStyles = makeStyles(() => ({
  pagination: {
    width: 'fit-content !important', 
    marginRight: '-5px !important',
    '& .MuiPaginationItem-root': {
      minWidth: 20,
      height: 20,
      padding: 0,
      margin: 0,
    },
    '& .MuiPaginationItem-text': {
      color: '#B4B4B4',
      '&.Mui-selected': {
        color: '#000000',
        backgroundColor: '#ffffff',
      },
    },
    '& .MuiPaginationItem-previousNext': {
      color: '#000000',
      '&.Mui-disabled': {
        color: '#B4B4B4',
      }
    }
  },
  faqText: {
    fontFamily: "Work Sans",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#39A2DB !important",
    marginTop: "18px",
    cursor: "pointer",
    marginLeft: '5px'
  },
  confirmText: {
    background: '#F1F0F5', 
    width: '100%', 
    height: 54, 
    borderRadius: '54px', 
    border: 'none',
    outline: 'none !important',
    color: '#000000',
    padding: '5px 27px',
    margin: '0px 70px',
  },
  stepContainer: {
    textTransform: 'capitalize',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: ' 18px !important',
    lineHeight: '18px !important',
  },
}));

const continueBtnStyle: React.CSSProperties = {
  borderRadius: "24px",
  width: "130px",
  height: "36px",
  color: "#fff",
  fontFamily: "Open Sans",
  fontStyle: "normal",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "18px",
  textTransform: "capitalize",
  padding: "10px",
  position: 'absolute',
  right: '20px',
  bottom: '30px',
}

interface PaymentReviewPropTypes {
  recentOTPs: TransactionDTO[];
  recentAutoPays: any[];
  open: boolean;
  onClose: () => void;
  submitPayment: () => void;
  back: () => void;
}

enum ModalStateEnum {
  HeadsUp = 'HeadsUp',
  Review = 'Review',
  Confirm = 'Confirm',
}

enum HistoryTabEnum {
  Otp = 'Otp',
  Ap = 'Ap',
}

const recordsPerPage = 3;

export default function PaymentReview(props: PaymentReviewPropTypes) {
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const style = { height: isOnMobileScreen ? "" : "675px" };
  const { recentOTPs, recentAutoPays } = props;

  const classes = useStyles();

  const [modalState, setModalState] = useState<ModalStateEnum>(ModalStateEnum.HeadsUp);

  const [accepted, setAccepted] = useState(false);
  const [otpPage, setOtpPage] = useState(1);
  const [otpCount, setOtpCount] = useState(0);
  const [listOfOtp, setListOfOtp] = useState<TransactionDTO[]>([]);
  const [listOfOtpShow, setListOfOtpShow] = useState<TransactionDTO[]>([]);
  const [apPage, setApPage] = useState(1);
  const [apCount, setApCount] = useState(0);
  const [listOfAp, setListOfAp] = useState<any[]>([]);
  const [listOfApShow, setListOfApShow] = useState<any[]>([]);
  
  useEffect(() => {
    if (recentOTPs?.length > 0) {
      setListOfOtp(recentOTPs);
      setOtpCount(Math.ceil(recentOTPs.length / recordsPerPage));
      setListOfOtpShow(recentOTPs.slice(0, recordsPerPage));
    }
  }, [recentOTPs])

  useEffect(() => {
    if (recentAutoPays?.length > 0) {
      setListOfAp(recentAutoPays);
      setApCount(Math.ceil(recentAutoPays.length / recordsPerPage));
      setListOfApShow(recentAutoPays.slice(0, recordsPerPage));
    }
  }, [recentAutoPays])
  
  const handleOtpChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setOtpPage(value);
    setListOfOtpShow(listOfOtp.slice((value - 1) * recordsPerPage, value * recordsPerPage));
  }

  const handleApChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setApPage(value);
    setListOfApShow(listOfAp.slice((value - 1) * recordsPerPage, value * recordsPerPage));
  }

  const handleBack = () => {
    if (modalState === ModalStateEnum.Confirm) {
      setModalState(ModalStateEnum.Review);
    } else if (modalState === ModalStateEnum.Review) {
      setModalState(ModalStateEnum.HeadsUp);
    } else {
      props.back();
      props.onClose();      
    }
  }

  const getHistoryHeadText = (text: string) => (
    <Typography
      style={{
        fontFamily: 'Work Sans',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "22px",
        color: "#14142B",
        marginBottom: "6px",
      }}
    >
      {text}
    </Typography>
  )

  const getHistoryBodyText = (text: string) => (
    <Typography
      style={{
        fontFamily: 'Work Sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "18px",
        color: "#14142B",
      }}
    >
      {text}
    </Typography>
  )

  const [confirmed, setConfirmed] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [phraseError, setPhraseError] = useState('');

  useEffect(() => {
    const availableTexts = [
      'This is not a duplicate payment, and I will not ask for a refund',
      'This is not a duplicate payment, and I will not ask for a refund.',
      'This is not a duplicate payment and I will not ask for a refund',
      'This is not a duplicate payment and I will not ask for a refund.',
    ];

    const confirmed = availableTexts.some((text) => text.toLocaleLowerCase() === confirmText.toLocaleLowerCase());
    setConfirmed(confirmed);
    setPhraseError(!confirmText ? '': confirmed ? '': 'The phrase is not accurate.');
  }, [confirmText])

  const [currentTab, setCurrentTab] = useState<HistoryTabEnum>(HistoryTabEnum.Otp);
  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: HistoryTabEnum) => {
    setCurrentTab(newValue);
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        fullScreen={isOnMobileScreen}
        BackdropProps={{
          invisible: true,
        }}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: "none",
          borderRadius: !isOnMobileScreen ? "24px" : undefined,
        }}
        PaperProps={{
          style: {
            pointerEvents: "auto",
            borderRadius: !isOnMobileScreen ? "24px" : undefined,
          },
        }}
      >
        <DialogContent sx={{ padding: '20px 0px', position: 'relative' }}>
          <Box sx={style}>
            <Grid container spacing={2} style={{ padding: '5px 24px 8px', }}>
              <Grid item xs={2}>
                <img src={RoundBackIcon} alt="close" style={{ cursor: "pointer", marginLeft: isOnMobileScreen ? "30px": "0px" }} onClick={handleBack} />
              </Grid>
              <Grid item xs={8}>
                <Typography id="payment-method-title" style={{ textAlign: "center" }}>
                  {modalState === ModalStateEnum.Review ? 'Review these recent or upcoming payments.' : 'Heads Up!'}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img src={RoundCloseIcon} alt="close" style={{ cursor: "pointer", marginLeft: isOnMobileScreen ? "0px" : "50px" }} onClick={props.onClose} />
              </Grid>
            </Grid>

            {modalState === ModalStateEnum.HeadsUp ? (
              <>
                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: "105px",
                    color: "#14142B",
                    textAlign: 'center',
                    marginTop: '100px',
                  }}
                >
                  STOP
                </Typography>

                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: "18px",
                    lineHeight: "22px",
                    color: "#000",
                    textAlign: 'center',
                    marginTop: '30px',
                    padding: '0px 30px',
                  }}
                >
                  It looks like you’ve already made or scheduled a payment.
                </Typography>

                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: "18px",
                    lineHeight: "22px",
                    color: "#000",
                    textAlign: 'center',
                    marginTop: '30px',
                    padding: '0px 30px',
                  }}
                >
                  Are you sure you want to proceed?
                </Typography>

                <Box sx={{ margin: '19px 16px', float: 'right' }}>
                  <Button
                    style={{
                      background: "#39A2DB",
                      ...continueBtnStyle,
                    }}
                    onClick={() => setModalState(ModalStateEnum.Review)}
                    id="confirmDupPayBtn1"
                  >
                    Continue
                  </Button>
                </Box>
              </>
            ) : modalState === ModalStateEnum.Review ? (
              <>
                <TabContext value={currentTab}>
                  <TabList onChange={handleChangeTabValue} sx={{ padding: '15px 70px 0px' }}>
                    <Tab label="History" value={HistoryTabEnum.Otp} className={classes.stepContainer} sx={{ textTransform: 'capitalize', width: '40%' }} />
                    <Tab label="Recurring Payments" value={HistoryTabEnum.Ap} className={classes.stepContainer} sx={{ textTransform: 'capitalize', width: '60%' }} />
                  </TabList>

                  <TabPanel value={HistoryTabEnum.Otp} sx={{ pl: 0, pr: 0 }}>
                    {otpCount > 1 && <Pagination count={otpCount} page={otpPage} onChange={handleOtpChange} className={classes.pagination} />}

                    <Box sx={{ borderTop: "1px solid #EEEEEE", minHeight: 250 }}>
                      {listOfOtpShow.map((trans: any) => (
                        <Grid container spacing={1} sx={{ marginTop: 0, padding: '5px 24px 13px', borderBottom: "1px solid #EEEEEE" }} key={trans.TransactionUuid}>
                          <Grid item xs={5}>{getHistoryHeadText('Status')}</Grid>
                          <Grid item xs={4}>{getHistoryHeadText('Date')}</Grid>
                          <Grid item xs={3}>{getHistoryHeadText('Amount')}</Grid>
                          <Grid item xs={5}>{getHistoryBodyText('Recent')}</Grid>
                          <Grid item xs={4}>{getHistoryBodyText(format(new Date(trans.TransactionDate), "MM/dd/yyyy"))}</Grid>
                          <Grid item xs={3}>{getHistoryBodyText(`$${formatNumber((trans?.Amount / 100).toFixed(2))}`)}</Grid>
                        </Grid>
                      ))}
                    </Box>                  
                  </TabPanel>

                  <TabPanel value={HistoryTabEnum.Ap} sx={{ pl: 0, pr: 0 }}>
                    {apCount > 1 && <Pagination count={apCount} page={apPage} onChange={handleApChange} className={classes.pagination} />}

                    <Box sx={{ borderTop: "1px solid #EEEEEE", minHeight: 250 }}>
                      {listOfApShow.map((trans: any) => (
                        <Grid container spacing={1} sx={{ marginTop: 0, padding: '5px 24px 13px', borderBottom: "1px solid #EEEEEE" }} key={trans.ApUuid}>
                          <Grid item xs={5}>{getHistoryHeadText('Status')}</Grid>
                          <Grid item xs={4}>{getHistoryHeadText('Next payment')}</Grid>
                          <Grid item xs={3}>{getHistoryHeadText('Amount')}</Grid>
                          <Grid item xs={5}>{getHistoryBodyText(trans.Status)}</Grid>
                          <Grid item xs={4}>{getHistoryBodyText(trans.FormattedNSPDate)}</Grid>
                          <Grid item xs={3}>{getHistoryBodyText(trans?.Type === "Fixed" ? `$${formatNumber((trans.FixedAmount / 100).toFixed(2))}` : trans?.Type === "Variable" ? 'My Full Balance' : "")}</Grid>
                        </Grid>
                      ))}
                    </Box>                  
                  </TabPanel>
                </TabContext>

                <Box sx={{ margin: '19px 16px', float: 'right' }}>
                  <Button
                    style={{
                      background: "#39A2DB",
                      ...continueBtnStyle,
                    }}
                    onClick={() => setModalState(ModalStateEnum.Confirm)}
                    id="confirmDupPayBtn1"
                  >
                    Continue
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#000",
                    textAlign: 'center',
                    marginTop: '30px',
                    padding: '0px 30px',
                  }}
                >
                  To proceed please type the following into this box:
                </Typography>

                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "italic",
                    fontWeight: "bold",
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#000",
                    textAlign: 'center',
                    marginTop: '10px',
                    padding: '0px 30px',
                  }}
                >
                  This is not a duplicate payment, and I will not ask for a refund.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <input type="text" className={classes.confirmText} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
                </Box>

                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontStyle: "normal",
                    fontSize: "12px",
                    lineHeight: "14px",
                    color: "#DB3939",
                    marginTop: "16px",
                    textAlign: 'center',
                  }}
                >
                  {phraseError}
                </Typography>

                <div style={{ width: '100%', height: 1, borderTop: '1px solid #EEEEEE', margin: '40px 0px 20px' }}></div>

                <Box sx={{ padding: '50px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <img id="confirmDupPayCheck" src={accepted ? CheckBox : UnCheckedBox} style={{ width: "20px", height: "20px" }} alt='Checkmark' onClick={()=>setAccepted(!accepted)} />
                    
                    <Typography 
                      style={{
                        fontFamily: "Work Sans",
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "18px",
                        marginLeft: '30px',
                      }}
                    >
                      By proceeding I acknowledge that I intend to make this additional payment and I accept financial responsibility. 
                      <br/><br/>
                      I understand that these funds will be held as a credit by my Property Manager in the event this payment is a duplicate.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ margin: '19px 16px', float: 'right' }}>
                  <Button
                    style={{
                      background: accepted && confirmed ? "#39A2DB": "#D3D3D3",
                      ...continueBtnStyle,
                    }}
                    disabled={!(accepted && confirmed)}
                    onClick={() => {
                      props.onClose();
                      props.submitPayment();
                    }}
                    id="confirmDupPayBtn"
                    className={accepted && confirmed ? 'active': ''}//it's for e2e test
                  >
                    Continue
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
